import React, { useEffect, useState } from 'react';
import axios from 'axios';

const STORAGE_KEY = 'nubes_age_verified_v1';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgeGate() {
  const [open, setOpen] = useState(false);
  const [siteName, setSiteName] = useState('NubesMTY');
  const [logoUrl, setLogoUrl] = useState('');
  const [logoHeight, setLogoHeight] = useState(72);
  const [leaving, setLeaving] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    // Only show if not previously verified in this browser
    try {
      const verified = localStorage.getItem(STORAGE_KEY);
      if (!verified) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    // Prevent scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Fetch dynamic branding
    axios
      .get(`${BACKEND_URL}/api/settings`)
      .then((r) => {
        if (r?.data?.site_name) setSiteName(r.data.site_name);
        if (r?.data?.logo_url) setLogoUrl(r.data.logo_url);
        if (r?.data?.logo_height) setLogoHeight(Number(r.data.logo_height) || 72);
      })
      .catch(() => {});
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const accept = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    window.dispatchEvent(new CustomEvent('nubesmty:age-confirmed'));
    setLeaving(true);
    setTimeout(() => setOpen(false), 320);
  };

  const reject = () => {
    setDenied(true);
    setTimeout(() => {
      window.location.href = 'https://www.google.com';
    }, 1600);
  };

  if (!open) return null;

  return (
    <div
      aria-modal="true"
      role="dialog"
      className={`fixed inset-0 z-[200] flex items-center justify-center px-4 py-6 transition-opacity duration-300 ${leaving ? 'opacity-0' : 'opacity-100'}`}
      data-testid="age-gate-overlay"
    >
      {/* Backdrop with grain + glow */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-xl" />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-orange-500/25 blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full bg-red-600/20 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full bg-orange-400/10 blur-[80px]" />
      </div>

      {/* Card */}
      <div
        className={`relative w-full max-w-md rounded-[28px] bg-gradient-to-b from-neutral-950 to-black border border-white/10 shadow-[0_30px_80px_-15px_rgba(249,115,22,0.35)] overflow-hidden transition-all duration-500 ${leaving ? 'scale-95 translate-y-4' : 'scale-100 translate-y-0'} animate-agegate-in`}
        data-testid="age-gate-card"
      >
        {/* Top orange strip */}
        <div className="h-1.5 bg-gradient-to-r from-orange-500 via-orange-400 to-red-500" />

        <div className="p-8 md:p-10 text-center">
          {/* Logo / cloud */}
          <div className="mx-auto flex items-center justify-center mb-6">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={siteName}
                style={{ height: `${Math.min(logoHeight, 84)}px` }}
                className="object-contain drop-shadow-[0_8px_20px_rgba(249,115,22,0.3)]"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/40">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-10 h-10 text-white">
                  <path d="M6.5 20a4.5 4.5 0 01-.35-8.987A5.5 5.5 0 0116 8.5a4.5 4.5 0 010 9H6.5z" />
                </svg>
              </div>
            )}
          </div>

          {/* 18+ badge */}
          <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-orange-300/90">
              Verificación de edad
            </span>
          </div>

          {!denied ? (
            <>
              <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
                Bienvenid<span className="text-orange-400">@</span>
              </h1>
              <p className="mt-3 text-white/70 text-[15px] leading-relaxed">
                Debes ser <strong className="text-white">mayor de edad</strong> para navegar en{' '}
                <strong className="text-orange-400">{siteName}</strong>.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-3">
                <button
                  onClick={accept}
                  className="group relative h-12 rounded-full bg-gradient-to-b from-orange-400 to-orange-600 text-black font-black tracking-wide text-sm shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                  data-testid="age-gate-accept-btn"
                >
                  <span className="inline-flex items-center gap-1.5">SI TENGO 18+ <span className="text-lg">✓</span></span>
                  <span className="absolute inset-0 rounded-full ring-1 ring-inset ring-white/20" aria-hidden />
                </button>
                <button
                  onClick={reject}
                  className="h-12 rounded-full bg-transparent border border-white/15 text-white/85 font-bold tracking-wide text-sm hover:bg-white/5 hover:border-white/25 hover:scale-[1.01] active:scale-[0.98] transition-all duration-200"
                  data-testid="age-gate-reject-btn"
                >
                  NO TENGO 18-
                </button>
              </div>

              <p className="mt-6 text-[11px] text-white/40 leading-relaxed">
                Al ingresar aceptas que la venta y consumo de vapes está prohibida a menores de edad.
                <br />Este sitio contiene nicotina.
              </p>
            </>
          ) : (
            <div className="py-4 animate-agegate-in" data-testid="age-gate-denied-state">
              <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-red-400">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <h2 className="text-xl font-black text-white">Lo sentimos</h2>
              <p className="mt-2 text-white/60 text-sm">
                Este sitio es exclusivo para mayores de edad. Serás redirigido en un momento.
              </p>
            </div>
          )}
        </div>

        {/* Bottom stripe */}
        <div className="border-t border-white/5 bg-white/[0.02] px-6 py-3 flex items-center justify-center gap-3 text-[10px] tracking-wider uppercase text-white/40">
          <span>Envío discreto</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span>Productos originales</span>
          <span className="w-1 h-1 rounded-full bg-white/20" />
          <span>+18</span>
        </div>
      </div>

      <style>{`
        @keyframes agegate-in {
          0% { opacity: 0; transform: translateY(24px) scale(0.94); filter: blur(6px); }
          60% { filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        .animate-agegate-in { animation: agegate-in 520ms cubic-bezier(0.16, 1, 0.3, 1) both; }
      `}</style>
    </div>
  );
}
